export type DocumentCloudProvider = 'google' | 'onedrive';

export type ExternalDocumentKind = 'pdf' | 'spreadsheet';

export interface ExternalDocumentDefinition {
    id: string;
    titleKey?: string;
    title?: string;
    kind: ExternalDocumentKind;
    googleViewUrl: string;
    oneDriveViewUrl: string;
}

export interface ExternalDocumentOverride {
    title?: string;
    url?: string;
}

const providerRaw = (process.env.REACT_APP_DOCUMENT_LINK_PROVIDER || 'google').toLowerCase();

export const DOCUMENT_LINK_PROVIDER: DocumentCloudProvider =
    providerRaw === 'onedrive' || providerRaw === 'one_drive' ? 'onedrive' : 'google';

const envGoogle = (suffix: string) => process.env[`REACT_APP_EXTERNAL_DOC_${suffix}_GOOGLE_URL`]?.trim() || '';
const envOneDrive = (suffix: string) => process.env[`REACT_APP_EXTERNAL_DOC_${suffix}_ONEDRIVE_URL`]?.trim() || '';

const catalog: ExternalDocumentDefinition[] = [
    {
        id: 'regca-citadel-im',
        titleKey: 'f:documents.items.regcaCitadelIm',
        kind: 'pdf',
        googleViewUrl: 'https://1drv.ms/b/c/59538eab9291486a/IQBwk8gpdIdERph7IO8v2clgAWnylX3WKRTzhSfUA4WVo7Y?e=MrDOUe',
        oneDriveViewUrl: 'https://1drv.ms/b/c/59538eab9291486a/IQBwk8gpdIdERph7IO8v2clgAWnylX3WKRTzhSfUA4WVo7Y?e=MrDOUe',
    },
    {
        id: 'regca-citadel-model',
        titleKey: 'f:documents.items.regcaCitadelModel',
        kind: 'spreadsheet',
        googleViewUrl: 'https://1drv.ms/x/c/59538eab9291486a/IQTt3E-Y5XVIQKuDkGuBLSEzAYhz4KRL6sSqFRwIrr4YvVQ?em=2&wdInConfigurator=True&wdInConfigurator=True&edaebf=rslc0',
        oneDriveViewUrl: 'https://1drv.ms/x/c/59538eab9291486a/IQTt3E-Y5XVIQKuDkGuBLSEzAYhz4KRL6sSqFRwIrr4YvVQ?em=2&wdInConfigurator=True&wdInConfigurator=True&edaebf=rslc0',
    },
    {
        id: 'sales-mis-citadel',
        titleKey: 'f:documents.items.salesMisCitadel',
        kind: 'spreadsheet',
        googleViewUrl: 'https://1drv.ms/x/c/59538eab9291486a/IQTX2fpyDkFwS5dn3FRHOjYiAbtKWtcDYVWG-wLVaFXHnWA?em=2&wdInConfigurator=True&wdInConfigurator=True&edaebf=rslc0',
        oneDriveViewUrl: 'https://1drv.ms/x/c/59538eab9291486a/IQTX2fpyDkFwS5dn3FRHOjYiAbtKWtcDYVWG-wLVaFXHnWA?em=2&wdInConfigurator=True&wdInConfigurator=True&edaebf=rslc0',
    },
    {
        id: 'propequity-mahindra-citadel',
        titleKey: 'f:documents.items.propequityMahindraCitadel',
        kind: 'spreadsheet',
        googleViewUrl: 'https://1drv.ms/x/c/59538eab9291486a/IQRulSVZe2h-S4klD-DUiwq7ASRHgpbnbaoX19zbBkrEdY4?em=2&wdInConfigurator=True&wdInConfigurator=True&edaebf=rslc0',
        oneDriveViewUrl: 'https://1drv.ms/x/c/59538eab9291486a/IQRulSVZe2h-S4klD-DUiwq7ASRHgpbnbaoX19zbBkrEdY4?em=2&wdInConfigurator=True&wdInConfigurator=True&edaebf=rslc0',
    },
];

function mergedUrl(
    catalogUrl: string,
    envValue: string,
): string {
    if (envValue) {
        return envValue;
    }
    return catalogUrl?.trim() || '';
}

export const EXTERNAL_DOCUMENTS: ExternalDocumentDefinition[] = catalog.map((d) => {
    const suffix = d.id.replace(/-/g, '_').toUpperCase();
    return {
        ...d,
        googleViewUrl: mergedUrl(d.googleViewUrl, envGoogle(suffix)),
        oneDriveViewUrl: mergedUrl(d.oneDriveViewUrl, envOneDrive(suffix)),
    };
});

const OVERRIDES_STORAGE_KEY = 'external_documents_overrides_v1';
const CUSTOM_DOCS_STORAGE_KEY = 'external_documents_custom_v1';

function hasWindow(): boolean {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function parseEmbedInput(raw: string): string {
    const value = (raw || '').trim();
    if (!value) return '';
    const srcMatch = value.match(/src\s*=\s*["']([^"']+)["']/i);
    return (srcMatch?.[1] || value).trim();
}

function normalizeId(raw: string): string {
    return (raw || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80);
}

function inferKindFromUrl(url: string): ExternalDocumentKind {
    const u = (url || '').toLowerCase();
    if (u.includes('/x/') || u.includes('spreadsheet') || u.endsWith('.xlsx') || u.endsWith('.xlsm') || u.endsWith('.xls')) {
        return 'spreadsheet';
    }
    return 'pdf';
}

export function getDocumentOverrides(): Record<string, ExternalDocumentOverride> {
    if (!hasWindow()) return {};
    try {
        const raw = window.localStorage.getItem(OVERRIDES_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return {};
        return parsed as Record<string, ExternalDocumentOverride>;
    } catch {
        return {};
    }
}

export function getDocumentOverrideById(id: string): ExternalDocumentOverride | undefined {
    const overrides = getDocumentOverrides();
    return overrides[id];
}

export function saveDocumentOverride(id: string, title: string, embedOrUrl: string): void {
    if (!hasWindow()) return;
    const overrides = getDocumentOverrides();
    const parsedUrl = parseEmbedInput(embedOrUrl);
    overrides[id] = {
        title: (title || '').trim(),
        url: parsedUrl,
    };
    window.localStorage.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(overrides));
}

export function deleteDocumentOverride(id: string): void {
    if (!hasWindow()) return;
    const overrides = getDocumentOverrides();
    if (overrides[id]) {
        delete overrides[id];
        window.localStorage.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(overrides));
    }
}

export function getCustomDocuments(): ExternalDocumentDefinition[] {
    if (!hasWindow()) return [];
    try {
        const raw = window.localStorage.getItem(CUSTOM_DOCS_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((d) => d && typeof d.id === 'string' && typeof d.kind === 'string');
    } catch {
        return [];
    }
}

export function saveCustomDocument(title: string, embedOrUrl: string): ExternalDocumentDefinition | null {
    if (!hasWindow()) return null;
    const parsedUrl = parseEmbedInput(embedOrUrl);
    const cleanTitle = (title || '').trim();
    if (!cleanTitle || !parsedUrl) return null;

    const customDocs = getCustomDocuments();
    const candidateId = normalizeId(cleanTitle);
    const idBase = candidateId || `doc-${Date.now()}`;
    let id = idBase;
    let seq = 1;
    const allIds = new Set([...EXTERNAL_DOCUMENTS, ...customDocs].map((d) => d.id));
    while (allIds.has(id)) {
        id = `${idBase}-${seq++}`;
    }

    const doc: ExternalDocumentDefinition = {
        id,
        title: cleanTitle,
        kind: inferKindFromUrl(parsedUrl),
        googleViewUrl: parsedUrl,
        oneDriveViewUrl: parsedUrl,
    };
    customDocs.push(doc);
    window.localStorage.setItem(CUSTOM_DOCS_STORAGE_KEY, JSON.stringify(customDocs));
    return doc;
}

export function deleteCustomDocument(id: string): void {
    if (!hasWindow()) return;
    const customDocs = getCustomDocuments();
    const next = customDocs.filter((d) => d.id !== id);
    window.localStorage.setItem(CUSTOM_DOCS_STORAGE_KEY, JSON.stringify(next));
    deleteDocumentOverride(id);
}

export function getExternalDocuments(): ExternalDocumentDefinition[] {
    return [...EXTERNAL_DOCUMENTS, ...getCustomDocuments()];
}

export function resolveDocumentViewUrl(doc: ExternalDocumentDefinition): string | null {
    const override = getDocumentOverrideById(doc.id);
    const overrideUrl = override?.url?.trim();
    if (overrideUrl) {
        return overrideUrl;
    }

    // Force this one sheet to use OneDrive regardless of provider/env Google overrides.
    if (doc.id === 'regca-citadel-model') {
        const forced = doc.oneDriveViewUrl?.trim() || doc.googleViewUrl?.trim();
        return forced || null;
    }

    const raw = DOCUMENT_LINK_PROVIDER === 'onedrive' ? doc.oneDriveViewUrl : doc.googleViewUrl;
    const t = raw?.trim();
    return t || null;
}

const SHEETS_ID = /\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/;

export function googleSheetsViewerEmbedUrl(url: string): string {
    const trimmed = url?.trim();
    if (!trimmed || DOCUMENT_LINK_PROVIDER !== 'google') {
        return trimmed;
    }
    const m = trimmed.match(SHEETS_ID);
    if (!m) {
        return trimmed;
    }
    const id = m[1];
    return `https://docs.google.com/spreadsheets/d/${id}/edit?usp=sharing&rm=minimal&widget=true&headers=false`;
}

export function isGoogleSheetsWorkbookUrl(url: string): boolean {
    return SHEETS_ID.test(url?.trim() || '');
}

export function getExternalDocumentById(id: string): ExternalDocumentDefinition | undefined {
    return getExternalDocuments().find((d) => d.id === id);
}
